import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { VideoTemplate, VideoTemplateFormData } from "../types/videoTemplate";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { toast } from "sonner";
import VideoTemplateForm from "../components/VideoTemplateForm";
import VideoTemplateList from "../components/VideoTemplateList";

const VideoTemplatePage: React.FC = () => {
  const [templates, setTemplates] = useState<VideoTemplate[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<VideoTemplate | null>(
    null
  );
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    template: VideoTemplate | null;
  }>({
    isOpen: false,
    template: null,
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("video_template")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Error fetching templates");
    } finally {
      setLoading(false);
    }
  };

  const getNextVideoTemplateId = async () => {
    const { data, error } = await supabase
      .from("video_template")
      .select("template_id")
      .ilike("template_id", "vt%")
      .order("created_at", { ascending: false })
      .limit(1);

    if (error) throw error;

    const lastId = data?.[0]?.template_id as string | undefined;
    if (!lastId) return "vt1";

    const lastNumber = Number(lastId.replace(/^vt/i, ""));
    return Number.isFinite(lastNumber) ? `vt${lastNumber + 1}` : "vt1";
  };

  const uploadOutputVideo = async (file: File): Promise<string | null> => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `video_templates/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("sceneon_image")
      .upload(filePath, file);

    if (uploadError) {
      console.error("Error uploading video:", uploadError);
      toast.error("Error uploading output video");
      return null;
    }

    const { data } = supabase.storage
      .from("sceneon_image")
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handleCreateTemplate = async (formData: VideoTemplateFormData) => {
    try {
      setLoading(true);

      // Validate and parse JSON prompt if provided
      let jsonPromptParsed: object | string | null = null;
      if (formData.json_prompt && formData.json_prompt.trim()) {
        try {
          jsonPromptParsed = JSON.parse(formData.json_prompt);
        } catch (parseError) {
          toast.error("Invalid JSON format in JSON Prompt field");
          throw new Error("Invalid JSON format");
        }
      }

      // Upload output video if provided
      let outputUrl: string | null = null;
      if (formData.output_video) {
        outputUrl = await uploadOutputVideo(formData.output_video);
        if (!outputUrl) throw new Error("Video upload failed");
      }

      const nextTemplateId = await getNextVideoTemplateId();

      const { data, error } = await supabase
        .from("video_template")
        .insert([
          {
            template_id: nextTemplateId,
            template_name: formData.template_name,
            owner: formData.owner || null,
            ai_model: formData.ai_model,
            json_prompt: jsonPromptParsed,
            ...(outputUrl && { output: [outputUrl] }),
          },
        ])
        .select();

      if (error) throw error;

      setTemplates([data[0], ...templates]);
      setShowForm(false);
      toast.success("Video template created successfully!");
    } catch (error) {
      console.error("Error creating template:", error);
      if (
        error instanceof Error &&
        error.message !== "Invalid JSON format" &&
        error.message !== "Video upload failed"
      ) {
        toast.error("Error creating template");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTemplate = async (formData: VideoTemplateFormData) => {
    if (!editingTemplate?.id) return;

    try {
      setLoading(true);

      // Validate and parse JSON prompt if provided
      let jsonPromptParsed: object | string | null = null;
      if (formData.json_prompt && formData.json_prompt.trim()) {
        try {
          jsonPromptParsed = JSON.parse(formData.json_prompt);
        } catch (parseError) {
          toast.error("Invalid JSON format in JSON Prompt field");
          throw new Error("Invalid JSON format");
        }
      }

      // Upload output video if a new file was selected
      let outputUrl: string | undefined;
      if (formData.output_video) {
        const url = await uploadOutputVideo(formData.output_video);
        if (!url) throw new Error("Video upload failed");
        outputUrl = url;
      }

      const updatePayload: Record<string, unknown> = {
        template_name: formData.template_name,
        owner: formData.owner || null,
        ai_model: formData.ai_model,
        json_prompt: jsonPromptParsed,
      };
      if (outputUrl !== undefined) {
        updatePayload.output = [outputUrl];
      }

      const { data, error } = await supabase
        .from("video_template")
        .update(updatePayload)
        .eq("id", editingTemplate.id)
        .select();

      if (error) throw error;

      setTemplates(
        templates.map((t) => (t.id === editingTemplate.id ? data[0] : t))
      );
      setEditingTemplate(null);
      setShowForm(false);
      toast.success("Video template updated successfully!");
    } catch (error) {
      console.error("Error updating template:", error);
      if (
        error instanceof Error &&
        error.message !== "Invalid JSON format" &&
        error.message !== "Video upload failed"
      ) {
        toast.error("Error updating template");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (template: VideoTemplate) => {
    setDeleteModal({ isOpen: true, template });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.template?.id) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from("video_template")
        .delete()
        .eq("id", deleteModal.template.id);

      if (error) throw error;

      setTemplates(templates.filter((t) => t.id !== deleteModal.template?.id));
      setDeleteModal({ isOpen: false, template: null });
      toast.success("Video template deleted successfully!");
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Error deleting template");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, template: null });
  };

  const handleEdit = (template: VideoTemplate) => {
    setEditingTemplate(template);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTemplate(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-800">Video Templates</h1>
        {!showForm && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-berrys-orange hover:bg-berrys-orange-dark text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Create Template
          </button>
        )}
      </div>

      {showForm && (
        <VideoTemplateForm
          onSubmit={
            editingTemplate ? handleUpdateTemplate : handleCreateTemplate
          }
          onCancel={handleCancel}
          loading={loading}
          initialData={editingTemplate}
        />
      )}

      {!showForm && templates.length === 0 && !loading ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <svg
            className="mx-auto h-16 w-16 text-gray-400 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No video templates found
          </h3>
          <p className="text-gray-500 mb-4">
            Get started by creating your first video template.
          </p>
        </div>
      ) : (
        !showForm && (
          <VideoTemplateList
            templates={templates}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
          />
        )
      )}

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Video Template"
        message="Are you sure you want to delete this video template? This action cannot be undone."
        itemName={deleteModal.template?.template_name || "template"}
        loading={loading}
      />
    </div>
  );
};

export default VideoTemplatePage;
